import { useMemo } from 'react';

import { useGetBusinessUnitByIdQuery } from '@xemplo/business-unit-query';
import { PAYRUN_STATUS_IDS, PayrunListItem, PersonaRole } from '@xemplo/gp-types';
import { useGetPayrollServiceProviderUsersLookupV2 } from '@xemplo/lookups-query';
import { useRoleAuthorisation } from '@xemplo/roles-query';
import { createNoResultList, ListBuilder } from '@xemplo/stateful-list-core';
import { TextAreaRhf } from '@xemplo/text-area';
import { V2 } from '@xemplo/text-dropdown';

import * as S from './assign-payroll-manager-modal-body.styles';

export function AssignPayrollManagerModalBody(props: { payrun: PayrunListItem }) {
  const { isInRole } = useRoleAuthorisation();
  const isSa = isInRole([PersonaRole.SystemAdmin]);
  const { data: buData } = useGetBusinessUnitByIdQuery({
    id: props.payrun.businessUnitId,
  });

  const payrollServiceProviderId = buData?.result.payrollServiceProviderId;

  const enableManagerField = isSa ? payrollServiceProviderId : true;

  return (
    <S.Container>
      <S.Description>
        Select a Payroll Manager to assign as the Primary Manager for this pay run. The
        assigned manager will appear in the Pay Run List and receive milestone
        notifications.
      </S.Description>
      {enableManagerField && (
        <S.FieldWrapper>
          <S.Label>Assigned Manager</S.Label>
          <ManagerDropdown
            payrun={props.payrun}
            payrollServiceProviderId={payrollServiceProviderId}
          />
        </S.FieldWrapper>
      )}

      <S.FieldWrapper>
        <S.Label>
          Note <S.OptionalText>(optional)</S.OptionalText>
        </S.Label>
        <TextAreaRhf
          name="notes"
          placeholder="Start typing here..."
          rows={4}
          aria-label="Note"
        />
      </S.FieldWrapper>
    </S.Container>
  );
}

export const ManagerDropdown = (props: {
  payrun: PayrunListItem;
  payrollServiceProviderId?: string;
}) => {
  const { isInRole } = useRoleAuthorisation();
  const isSa = isInRole([PersonaRole.SystemAdmin]);

  const payrollServiceProviderId = props.payrollServiceProviderId;

  const { data } = useGetPayrollServiceProviderUsersLookupV2({
    filter: {
      payrollServiceProviderId: isSa ? payrollServiceProviderId : undefined,
    },
    per_page: 500,
  });

  const items = useMemo(() => {
    if (!data?.parsedItems?.length) {
      return createNoResultList({ label: 'No users available' });
    }
    return ListBuilder.create()
      .addGroup((group) => {
        data.parsedItems.forEach((row) => group.addItem(row.key, row.value));
        return group;
      })
      .build();
  }, [data]);

  const payrunIsCompleted =
    props.payrun.payrunStatusId === PAYRUN_STATUS_IDS.COMPLETED ||
    props.payrun.payrunStatusId === PAYRUN_STATUS_IDS.PROCESSED;

  return (
    <V2.TextDropdownRhf
      name="assigneeUserId"
      items={items}
      disabled={payrunIsCompleted}
      placeholder="Select a User"
      allowDeselect
      searchable
      aria-label="Select assigned manager"
    />
  );
};
